import type { Container } from '@packages/core/container';

import { StsBackendClient } from '@packages/clients';
import { ANALYTICS_EVENTS, AnalyticsService } from '@packages/core/analytics';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { buildDtcCodePatchBody, type DtcCodeUpdateBodyParams } from '../helpers';

/**
 * Add a new DTC code to a Techline case via the NorthStar integration and log the event to analytics.
 */
export class AddDtcCode extends StsBackendOperation {
    /**
     * Register this operation with the backend client and analytics service dependencies.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        container.register(AddDtcCode, [StsBackendClient, AnalyticsService]);
    }

    /** The analytics service for logging DTC events. */
    private analyticsService: AnalyticsService;

    /**
     * @param client - the backend API client
     * @param analyticsService - the analytics service for logging events
     */
    constructor(client: StsBackendClient, analyticsService: AnalyticsService) {
        super(client);
        this.analyticsService = analyticsService;
    }

    /**
     * Execute the add DTC code operation.
     *
     * @param params - the case number, DTC code, and optional fields
     * @returns true if the DTC was successfully added, false otherwise
     */
    async exec(params: DtcCodeUpdateBodyParams): Promise<boolean> {
        const { caseNumber, code } = params;
        const updateBody = buildDtcCodePatchBody(caseNumber, code);

        const response = await this.client.updateTechlineCaseDtcCode({ updateBody });

        if (response?.success) {
            this.analyticsService.logEvent(ANALYTICS_EVENTS.USER_ADDS_DTC);
            return true;
        }

        return false;
    }
}
